/**
 * plan-grouping.js — collapse carrier-returned variants of the same plan
 * into one group so the card can switch deductible/coinsurance tiers
 * instantly from already-fetched data. Pure functions, no DOM.
 */

const COINS_RE = /\b(\d{2,3}\/\d{1,3})\b|\b(\d{1,3}\s?%)/; // "80/20" or "70%" — for .match()
const COINS_RE_G = /\b(\d{2,3}\/\d{1,3})\b|\b(\d{1,3}\s?%)/g; // global — for .replace()
const MONEY_RE = /\$\s?[\d,]+/g;

/**
 * Derive grouping axes for a plan.
 * @param {object} plan
 * @returns {{family:string, deductible:number, coinsurance:(string|null)}}
 */
export function deriveVariantAxes(plan) {
  if (!plan) return { family: '', deductible: 0, coinsurance: null };
  const rawName = (plan && plan.name) ? String(plan.name) : '';
  const coinsMatch = rawName.match(COINS_RE);
  const coinsurance =
    plan.coinsurance != null ? String(plan.coinsurance)
    : plan.planDetails && plan.planDetails.coinsurance != null ? String(plan.planDetails.coinsurance)
    : coinsMatch ? (coinsMatch[1] || coinsMatch[2].replace(/\s/g, ''))
    : null;

  let family = rawName
    .replace(MONEY_RE, ' ')
    .replace(COINS_RE_G, ' ')
    .replace(/\bdeductible\b/ig, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!family) family = String((plan && plan.id) != null ? plan.id : '');

  const deductible = parseFloat(String(plan.deductible ?? 0).replace(/[$,]/g, '')) || 0;
  return { family, deductible, coinsurance };
}

/**
 * Group plans into variant families. Safe-by-default: only collapses plans
 * sharing carrier + type + normalized family name.
 * @param {Array<object>} plans
 * @returns {Array<object>} groups
 */
export function groupPlanVariants(plans) {
  const byKey = new Map();
  for (const plan of (plans || [])) {
    if (!plan) continue;
    const { family, deductible, coinsurance } = deriveVariantAxes(plan);
    const carrier = plan.carrier || plan.network || 'LifeX';
    const type = plan.type || plan.category || '';
    const key = carrier + '|' + type + '|' + family;
    const variant = {
      planId: String(plan.id),
      deductible,
      coinsurance,
      premium: Number(plan.calculatedPremium) || 0,
      plan,
    };
    if (!byKey.has(key)) {
      byKey.set(key, { key, carrier, type, family, variants: [] });
    }
    byKey.get(key).variants.push(variant);
  }

  const groups = [];
  for (const group of byKey.values()) {
    group.variants.sort((a, b) => a.premium - b.premium);
    group.defaultVariantId = group.variants[0].planId;
    group.deductibles = [...new Set(group.variants.map(v => v.deductible))].sort((a, b) => a - b);
    // order follows the premium sort above (cheapest variant first)
    group.coinsurances = [...new Set(group.variants.map(v => v.coinsurance).filter(Boolean))];
    group.representative = group.variants[0].plan;
    groups.push(group);
  }
  return groups;
}

/** Group key for a single plan — must match groupPlanVariants' key exactly. */
export function groupKeyFor(plan) {
  const { family } = deriveVariantAxes(plan);
  const carrier = (plan && (plan.carrier || plan.network)) || 'LifeX';
  const type = (plan && (plan.type || plan.category)) || '';
  return carrier + '|' + type + '|' + family;
}

/** Find a variant by deductible + coinsurance; null if that combo wasn't returned. */
export function findVariant(group, deductible, coinsurance) {
  return group.variants.find(v =>
    v.deductible === deductible && (coinsurance == null || v.coinsurance === coinsurance)
  ) || null;
}
